const API_BASE_URL = process.env.VUE_APP_API_URL || '/api'

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    const defaultHeaders = {}
    
    // Set Content-Type based on body type
    if (options.method && ['POST', 'PUT', 'PATCH'].includes(options.method) && options.body) {
      // If body is already a string (JSON), don't override Content-Type
      // If body is FormData or URLSearchParams, set appropriate type
      if (typeof options.body === 'string') {
        // JSON string - Content-Type should be set by caller or default to JSON
        if (!options.headers || !options.headers['Content-Type']) {
          defaultHeaders['Content-Type'] = 'application/json'
        }
      } else {
        // FormData or URLSearchParams
        defaultHeaders['Content-Type'] = 'application/x-www-form-urlencoded'
      }
    }
    
    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: 'include', // Important for cookies
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        // Try to parse error JSON, but handle empty responses
        let errorDetail = response.statusText
        try {
          const errorText = await response.text()
          if (errorText) {
            const errorJson = JSON.parse(errorText)
            errorDetail = errorJson.detail || errorJson.message || errorDetail
          }
        } catch (e) {
          // Response is not JSON, use status text
        }
        throw new Error(errorDetail || `HTTP error! status: ${response.status}`)
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type')
      const contentLength = response.headers.get('content-length')
      
      // If no content or empty response, return null (for CookieTransport)
      if (contentLength === '0' || response.status === 204) {
        return null
      }

      // Try to parse JSON only if content-type indicates JSON
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text()
        return text ? JSON.parse(text) : null
      }

      // For other content types or empty responses, return null
      return null
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  async login(username, password) {
    const formData = new URLSearchParams()
    formData.append('username', username)
    formData.append('password', password)

    // CookieTransport doesn't return JSON - it sets cookies
    // Just check if request is successful (status 200/204)
    await this.request('/auth/auth/jwt/login', {
      method: 'POST',
      body: formData,
    })
    
    // With CookieTransport, successful login means cookies are set
    // Return success indicator
    return { success: true }
  }

  async logout() {
    // CookieTransport logout also doesn't return JSON
    await this.request('/auth/auth/jwt/logout', {
      method: 'POST',
    })
    return { success: true }
  }

  async getAllUsers() {
    return this.request('/manage-users/all', {
      method: 'GET',
    })
  }

  async getAllMembers() {
    return this.request('/manage-members/all', {
      method: 'GET',
    })
  }

  async createMember(memberData) {
    return this.request('/manage-members', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(memberData),
    })
  }

  async updateMember(userId, memberData) {
    return this.request(`/manage-members/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(memberData),
    })
  }

  async deleteMember(userId) {
    return this.request(`/manage-members/${userId}`, {
      method: 'DELETE',
    })
  }

  async updateUser(userId, userData) {
    return this.request(`/manage-users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    })
  }

  async createUser(userData) {
    // Use the same endpoint as registration: /auth/auth/register
    return this.request('/auth/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    })
  }

  async activateUser(userId) {
    return this.request(`/manage-users/${userId}/activate`, {
      method: 'PATCH',
    })
  }

  async deleteUser(userId) {
    return this.request(`/manage-users/${userId}`, {
      method: 'DELETE',
    })
  }
}

export default new ApiService()

